import NextAuth from 'next-auth';
import Keycloak from 'next-auth/providers/keycloak';
import type { JWT } from 'next-auth/jwt';
import * as Sentry from '@sentry/nextjs';
import {
  performTokenRefresh,
  shouldAttemptRefresh,
  type RefreshFailureReport,
} from './token-refresh';
import './types';

/**
 * Central next-auth configuration for Keycloak OIDC authentication.
 *
 * Flow:
 * 1. User visits any protected page
 * 2. Middleware redirects to /api/auth/signin -> Keycloak login
 * 3. After login, Keycloak redirects back with authorization code
 * 4. next-auth exchanges code for tokens (server-side, confidential client)
 * 5. JWT callback stores access_token, refresh_token, customerNumber, roles
 * 6. Session callback exposes accessToken + user claims to the client
 * 7. Token refresh happens server-side when access_token expires
 *
 * Fehlschlägt der Refresh, entscheidet `token-refresh.ts`, ob es einen
 * weiteren Versuch wert ist. Nur ein endgültiger Fehlschlag setzt
 * `session.error` — ein Netzwerk-Schluckauf tut es nicht.
 */
export const { handlers, signIn, signOut, auth } = NextAuth({
  providers: [
    Keycloak({
      clientId: process.env.AUTH_KEYCLOAK_ID,
      clientSecret: process.env.AUTH_KEYCLOAK_SECRET,
      issuer: process.env.AUTH_KEYCLOAK_ISSUER,
    }),
  ],
  pages: {
    signIn: '/login',
  },
  callbacks: {
    // Middleware uses this to decide: redirect to login or allow through
    authorized({ auth }) {
      return !!auth?.user;
    },

    async jwt({ token, account, profile }) {
      // First login: store Keycloak tokens and claims
      if (account) {
        token.sessionStartedAt = Math.floor(Date.now() / 1000);
        token.accessToken = account.access_token;
        token.refreshToken = account.refresh_token;
        token.expiresAt = account.expires_at;
        // Wird ausschließlich als `id_token_hint` beim RP-initiated Logout
        // gebraucht (Kontowechsel, siehe switch-account.ts). Ohne den Hint
        // verlangt Keycloak eine Abmelde-Bestätigungsseite.
        token.idToken = account.id_token;

        // Extract customerNumber from the ID token / profile
        // Keycloak maps user attributes to token claims
        const profileAny = profile as Record<string, unknown> | undefined;
        token.customerNumber =
          (profileAny?.customerNumber as string) ??
          (profileAny?.customer_number as string) ??
          undefined;

        // Extract roles from the access token. Keycloak ships realm roles in
        // `realm_access` and client roles in `resource_access`. The backend
        // AuthGuard treats both the realm `admin`/`shop_analytics` roles AND the
        // `baustoffe.de` client `admin` role as privileged (exempt from the
        // customerNumber requirement), so we merge both into `roles` to keep the
        // client's privilege check in sync with the API.
        if (account.access_token) {
          try {
            const payload = JSON.parse(
              Buffer.from(account.access_token.split('.')[1], 'base64').toString()
            );
            const realmRoles: string[] = payload.realm_access?.roles ?? [];
            const clientRoles: string[] =
              payload.resource_access?.['baustoffe.de']?.roles ?? [];
            token.roles = [...new Set([...realmRoles, ...clientRoles])];
          } catch {
            token.roles = [];
          }
        } else {
          // Fallback: try profile (less reliable)
          const realmAccess = profileAny?.realm_access as
            | { roles?: string[] }
            | undefined;
          token.roles = realmAccess?.roles ?? [];
        }
      }

      // Refresh nötig? `shouldAttemptRefresh` berücksichtigt neben dem Ablauf
      // auch das Wartefenster nach einem Fehlschlag und einen bereits
      // endgültig gescheiterten Refresh.
      if (shouldAttemptRefresh(token, Math.floor(Date.now() / 1000))) {
        return refreshAccessToken(token);
      }

      return token;
    },

    async session({ session, token }) {
      // Expose access token and user claims to the client
      session.accessToken = token.accessToken as string;
      // Durchgereicht, damit die App einen endgültig toten Refresh erkennen
      // kann, statt nur „Unauthorized" von der API zu sehen.
      session.error = token.error as string | undefined;
      session.idToken = token.idToken as string | undefined;
      session.user.customerNumber = token.customerNumber as string | undefined;
      session.user.roles = (token.roles as string[]) ?? [];
      return session;
    },
  },
});

/**
 * Refresht das Keycloak-Access-Token. Wirft nie — jeder Fehlschlag kommt als
 * Token-Zustand zurück, damit next-auth weiterarbeiten kann.
 *
 * Die Entscheidungslogik (vorübergehend vs. endgültig, Backoff, Aufgeben)
 * liegt in `token-refresh.ts` und ist dort getestet. Hier bleibt nur die
 * Verdrahtung mit der Umgebung und mit Sentry.
 */
async function refreshAccessToken(token: JWT): Promise<JWT> {
  const issuer = process.env.AUTH_KEYCLOAK_ISSUER;

  const refreshed = await performTokenRefresh(token, {
    tokenUrl: `${issuer}/protocol/openid-connect/token`,
    clientId: process.env.AUTH_KEYCLOAK_ID!,
    clientSecret: process.env.AUTH_KEYCLOAK_SECRET!,
    fetchImpl: fetch,
    nowSeconds: () => Math.floor(Date.now() / 1000),
    onFailure: (report) => reportRefreshFailure(report, token),
  });

  return { ...token, ...refreshed };
}

/**
 * Meldet einen fehlgeschlagenen Refresh an Sentry.
 *
 * WARUM: vorher stand hier `console.error`. Im Produktionsbetrieb (zwei Pods)
 * ist diese Ausgabe praktisch unsichtbar, weshalb bis jetzt niemand wusste,
 * WARUM der Refresh scheitert — sichtbar war nur die Folge im Frontend
 * („CombinedGraphQLErrors: Unauthorized").
 *
 * Ein vorübergehender Fehlschlag wird nur als Log geschrieben (kein Issue,
 * er heilt sich selbst); erst das endgültige Aufgeben erzeugt ein Event.
 */
function reportRefreshFailure(report: RefreshFailureReport, token: JWT): void {
  const nowSeconds = Math.floor(Date.now() / 1000);
  const diagnostics = {
    kind: report.kind,
    status: report.status,
    errorCode: report.errorCode,
    errorDescription: report.errorDescription,
    cause: report.cause,
    attempt: report.attempt,
    // Wie lange war das Access-Token schon abgelaufen? Ein großer Wert deutet
    // auf einen lange ruhenden Tab, ein kleiner auf einen echten Störfall.
    secondsSinceExpiry:
      token.expiresAt != null ? nowSeconds - token.expiresAt : undefined,
    // Session-Alter: unterscheidet „SSO-Session-Höchstdauer erreicht" (immer
    // derselbe Wert) von sporadischen Störungen (gestreute Werte).
    sessionAgeSeconds:
      token.sessionStartedAt != null
        ? nowSeconds - token.sessionStartedAt
        : undefined,
  };

  if (!report.givingUp) {
    Sentry.logger.warn(
      '[TokenRefresh] Refresh fehlgeschlagen, weiterer Versuch folgt',
      diagnostics,
    );
    return;
  }

  Sentry.captureMessage(
    '[TokenRefresh] Refresh endgültig gescheitert — Session braucht Neuanmeldung',
    {
      level: 'error',
      tags: {
        'crm.refreshFailureKind': report.kind,
        'crm.refreshErrorCode': report.errorCode ?? 'none',
        ...(report.status != null
          ? { 'crm.refreshHttpStatus': report.status }
          : {}),
      },
      extra: diagnostics,
      fingerprint: ['crm-token-refresh-permanent-failure', report.errorCode ?? 'unknown'],
    },
  );
}
